import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";

interface MediaPreviewModalProps {
  url: string | null;
  fileName?: string;
  type?: "image" | "file" | "video" | "audio";
  onClose: () => void;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.6), transparent);
  z-index: 10;
`;

const FileName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 16px;
  box-sizing: border-box;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  animation: ${scaleIn} 0.2s ease;
  user-select: none;
  -webkit-user-drag: none;
`;

const FilePreviewBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px;
  background: #1e1e1e;
  border-radius: 16px;
  animation: ${scaleIn} 0.2s ease;
  max-width: 300px;
  width: 100%;
`;

const FileIcon = styled.div`
  font-size: 56px;
`;

const FileNameText = styled.div`
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  word-break: break-all;
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #2196f3;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 12px;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
`;

const getFileIcon = (fileName?: string): string => {
  if (!fileName) return "📄";
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (["pdf"].includes(ext || "")) return "📋";
  if (["doc", "docx"].includes(ext || "")) return "📝";
  if (["xls", "xlsx"].includes(ext || "")) return "📊";
  if (["zip", "rar", "7z"].includes(ext || "")) return "🗜️";
  if (["mp4", "mov", "avi"].includes(ext || "")) return "🎬";
  if (["mp3", "wav", "ogg"].includes(ext || "")) return "🎵";
  return "📄";
};

const isImageUrl = (url: string): boolean => {
  const clean = url.split("?")[0].toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(clean);
};

const MediaPreviewModal: React.FC<MediaPreviewModalProps> = ({
  url,
  fileName,
  type,
  onClose,
}) => {
  // Disable body scroll saat modal terbuka
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Tutup dengan tombol back (Android)
  useEffect(() => {
    const handlePopState = () => onClose();
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [onClose]);

  if (!url) return null;

  const showAsImage =
    type === "image" || (type !== "file" && isImageUrl(url));

  return (
    <Overlay onClick={onClose}>
      <Header onClick={(e) => e.stopPropagation()}>
        <FileName>{fileName || (showAsImage ? "Gambar" : "File")}</FileName>
        <CloseButton onClick={onClose}>✕</CloseButton>
      </Header>

      {showAsImage ? (
        <ImageWrapper onClick={(e) => e.stopPropagation()}>
          <PreviewImage
            src={url}
            alt={fileName || "preview"}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://via.placeholder.com/300x300?text=Gagal+memuat";
            }}
          />
        </ImageWrapper>
      ) : (
        <FilePreviewBox onClick={(e) => e.stopPropagation()}>
          <FileIcon>{getFileIcon(fileName)}</FileIcon>
          <FileNameText>{fileName || "File"}</FileNameText>
          <DownloadButton
            href={url}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
          >
            ⬇ Download File
          </DownloadButton>
        </FilePreviewBox>
      )}
    </Overlay>
  );
};

export default MediaPreviewModal;